/**
 * Admin API Client
 * 
 * API functions for the SpeyBooks admin panel.
 * All endpoints require admin privileges.
 * 
 * v2.6.0 additions:
 * - Billing: cancelSubscription, refundPayment, retryPayment
 * - Users: sendResetLink, suspendUser, unsuspendUser, changeRole, deleteUser
 * - Orgs: deleteOrg
 * - System: getFailedWebhooks, replayWebhooks, clearCache
 */

import api, { ApiResponse } from './api';

// ============================================================================
// Types
// ============================================================================

export interface AdminStats {
  users: {
    total: number;
    active: number;
    locked: number;
  };
  organisations: {
    total: number;
    trial: number;
    soleTrader: number;
    limitedCompany: number;
  };
  trials: {
    active: number;
    expiringSoon: number;
    expired: number;
  };
  revenue: {
    mrr: number;
    mrrFormatted: string;
    soleTraderActive: number;
    limitedActive: number;
  };
  recentSignups: Array<{
    id: number;
    email: string;
    fullName: string;
    createdAt: string;
    orgName: string;
    plan: string;
  }>;
  webhookHealth: {
    total: number;
    processed: number;
    failed: number;
    status: 'healthy' | 'warning';
  };
}

export interface AdminUser {
  id: number;
  email: string;
  fullName: string;
  isActive: boolean;
  isAdmin: boolean;
  totpEnabled: boolean;
  failedAttempts: number;
  lockedUntil: string | null;
  lastLogin: string | null;
  createdAt: string;
  orgId: string | null;
  orgName: string | null;
  orgPlan: string | null;
  // v2.6.0 additions
  suspendedAt?: string | null;
  suspendedReason?: string | null;
  suspendedPermanent?: boolean;
}

export interface AdminUserDetail extends AdminUser {
  auditLog: Array<{
    action: string;
    createdAt: string;
    details: Record<string, unknown>;
  }>;
  role?: string;
}

export interface AdminOrg {
  id: string;
  name: string;
  slug: string;
  plan: string;
  subscriptionStatus: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  lastActivityAt: string | null;
  createdAt: string;
  userCount: number;
}

export interface AdminOrgDetail extends AdminOrg {
  users: Array<{
    id: number;
    email: string;
    fullName: string;
    role: string;
    isActive: boolean;
  }>;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  invoiceCount?: number;
  transactionCount?: number;
}

export interface SystemSetting {
  key: string;
  value: unknown;
  description: string;
  updatedAt: string | null;
  updatedBy: number | null;
}

export interface SystemHealth {
  database: {
    status: 'healthy' | 'warning' | 'error';
    latencyMs: number;
    pool: {
      totalCount: number;
      idleCount: number;
      waitingCount: number;
    };
  };
  webhooks: {
    status: 'healthy' | 'warning';
    last24h: {
      total: number;
      processed: number;
      failed: number;
      pending: number;
    };
    recentErrors: Array<{
      source: string;
      event_type: string;
      error_details: string;
      created_at: string;
    }>;
  };
  timestamp: string;
}

export interface AuditLogEntry {
  id: number;
  adminId: number;
  adminEmail: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  details: Record<string, unknown>;
  ipAddress: string | null;
  createdAt: string;
}

export interface SearchResult {
  type: 'user' | 'organisation' | 'invoice';
  id: string | number;
  label: string;
  sublabel: string;
  isActive?: boolean;
  status?: string;
}

export interface SearchResults {
  users: SearchResult[];
  organisations: SearchResult[];
  invoices: SearchResult[];
}

export interface ImpersonationResult {
  token: string;
  expiresAt: string;
  targetUser: {
    id: number;
    email: string;
  };
}

// v2.6.0 types
export interface FailedWebhook {
  id: number;
  source: string;
  eventType: string;
  payload: Record<string, unknown>;
  errorDetails: string | null;
  retryCount: number;
  createdAt: string;
}

export interface RefundResult {
  refundId: string;
  amount: number;
  currency: string;
  status: string;
}

export interface RetryPaymentResult {
  invoiceId: string;
  status: string;
  amountPaid: number;
  paid: boolean;
}

// ============================================================================
// API Functions
// ============================================================================

export const adminApi = {
  // ==========================================================================
  // Dashboard
  // ==========================================================================
  
  getStats: async () => {
    const response = await api.get<ApiResponse<AdminStats>>('/admin/stats');
    return response.data;
  },

  // ==========================================================================
  // Users
  // ==========================================================================
  
  getUsers: async (params?: {
    page?: number;
    perPage?: number;
    search?: string;
    status?: 'all' | 'active' | 'locked' | 'inactive' | 'suspended';
    isAdmin?: 'all' | 'true' | 'false';
  }) => {
    const response = await api.get<ApiResponse<{ users: AdminUser[] }>>('/admin/users', { params });
    return response.data;
  },

  getUser: async (id: number) => {
    const response = await api.get<ApiResponse<AdminUserDetail>>(`/admin/users/${id}`);
    return response.data;
  },

  unlockUser: async (id: number) => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/admin/users/${id}/unlock`);
    return response.data;
  },

  disableMfa: async (id: number) => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/admin/users/${id}/disable-mfa`);
    return response.data;
  },

  impersonateUser: async (id: number, reason: string) => {
    const response = await api.post<ApiResponse<ImpersonationResult>>(`/admin/users/${id}/impersonate`, { reason });
    return response.data;
  },

  // v2.6.0 - Send password reset link
  sendResetLink: async (id: number) => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/admin/users/${id}/send-reset-link`);
    return response.data;
  },

  // v2.6.0 - Suspend/ban user
  suspendUser: async (id: number, params: { reason: string; permanent?: boolean }) => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/admin/users/${id}/suspend`, params);
    return response.data;
  },

  // v2.6.0 - Unsuspend user
  unsuspendUser: async (id: number) => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/admin/users/${id}/unsuspend`);
    return response.data;
  },

  // v2.6.0 - Change user's org role
  changeUserRole: async (id: number, role: 'owner' | 'admin' | 'member' | 'viewer') => {
    const response = await api.patch<ApiResponse<{ message: string }>>(`/admin/users/${id}/role`, { role });
    return response.data;
  },

  // v2.6.0 - Delete user (GDPR)
  deleteUser: async (id: number, reason: string) => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/admin/users/${id}`, {
      data: { confirm: 'DELETE', reason },
    });
    return response.data;
  },

  // ==========================================================================
  // Organisations
  // ==========================================================================
  
  getOrgs: async (params?: {
    page?: number;
    perPage?: number;
    search?: string;
    plan?: 'all' | 'trial' | 'sole_trader' | 'limited_company';
    status?: 'all' | 'trialing' | 'active' | 'past_due' | 'canceled';
  }) => {
    const response = await api.get<ApiResponse<{ organisations: AdminOrg[] }>>('/admin/orgs', { params });
    return response.data;
  },

  getOrg: async (id: string) => {
    const response = await api.get<ApiResponse<AdminOrgDetail>>(`/admin/orgs/${id}`);
    return response.data;
  },

  updateOrg: async (id: string, data: {
    plan?: 'trial' | 'sole_trader' | 'limited_company';
    trialDays?: number;
    subscriptionStatus?: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid';
  }) => {
    const response = await api.patch<ApiResponse<{ message: string }>>(`/admin/orgs/${id}`, data);
    return response.data;
  },

  // v2.6.0 - Cancel subscription
  cancelSubscription: async (id: string, params: { immediately?: boolean; reason: string }) => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/admin/orgs/${id}/cancel-subscription`, params);
    return response.data;
  },

  // v2.6.0 - Refund payment
  refundPayment: async (id: string, params: { amount?: number; reason: string }) => {
    const response = await api.post<ApiResponse<RefundResult>>(`/admin/orgs/${id}/refund`, params);
    return response.data;
  },

  // v2.6.0 - Retry payment
  retryPayment: async (id: string, invoiceId?: string) => {
    const response = await api.post<ApiResponse<RetryPaymentResult>>(`/admin/orgs/${id}/retry-payment`, { invoiceId });
    return response.data;
  },

  // v2.6.0 - Delete organisation (GDPR)
  deleteOrg: async (id: string, reason: string) => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/admin/orgs/${id}`, {
      data: { confirm: 'DELETE', reason },
    });
    return response.data;
  },

  // ==========================================================================
  // Settings
  // ==========================================================================
  
  getSettings: async () => {
    const response = await api.get<ApiResponse<{ settings: SystemSetting[] }>>('/admin/settings');
    return response.data;
  },

  updateSetting: async (key: string, value: unknown) => {
    const response = await api.patch<ApiResponse<SystemSetting>>(`/admin/settings/${key}`, { value });
    return response.data;
  },

  // ==========================================================================
  // System Health
  // ==========================================================================
  
  getHealth: async () => {
    const response = await api.get<ApiResponse<SystemHealth>>('/admin/system/health');
    return response.data;
  },

  // v2.6.0 - Get failed webhooks
  getFailedWebhooks: async () => {
    const response = await api.get<ApiResponse<{ webhooks: FailedWebhook[] }>>('/admin/system/webhooks/failed');
    return response.data;
  },

  // v2.6.0 - Replay failed webhooks
  replayWebhooks: async (webhookIds: number[]) => {
    const response = await api.post<ApiResponse<{ message: string; webhookIds: number[] }>>('/admin/system/replay-webhooks', { webhookIds });
    return response.data;
  },

  // v2.6.0 - Clear cache
  clearCache: async () => {
    const response = await api.post<ApiResponse<{ message: string; keysCleared: number }>>('/admin/system/clear-cache');
    return response.data;
  },

  // ==========================================================================
  // Search
  // ==========================================================================
  
  search: async (query: string) => {
    const response = await api.get<ApiResponse<SearchResults>>('/admin/search', { params: { q: query } });
    return response.data;
  },

  // ==========================================================================
  // Audit Log
  // ==========================================================================
  
  getAuditLog: async (params?: { page?: number; perPage?: number }) => {
    const response = await api.get<ApiResponse<{ logs: AuditLogEntry[] }>>('/admin/audit-log', { params });
    return response.data;
  },
};

export default adminApi;
